<template>
<section id="pricing" class="section pricing-two bg-img">
    <div class="container">
        <div class="row justify-content-center">
            <div class="col-12 col-xl-6">
                <div class="section__header wow fadeInUp" data-wow-duration="600ms" data-wow-delay="300ms">
                    <h2 class="h2">{{ labelsState['ready_to_start'] || '¿Ready to start?' }}</h2>
                    <h2 class="h2">{{ labelsState['start_today_free'] || 'Start today totally free' }}</h2>
                </div>
            </div>
        </div>
        <div class="row justify-content-center mb-4">
            <div class="col-auto">
                <button 
                    class="btn btn--filter" 
                    :class="{ active: filter === 'all' }" 
                    @click="filter = 'all'; applyFilter();">
                    {{ labelsState['all_plans'] || 'All Plans' }}
                </button>
                <button 
                    class="btn btn--filter" 
                    :class="{ active: filter === 'free' }" 
                    @click="filter = 'free'; applyFilter();">
                    {{ labelsState['free'] || 'Free' }}
                </button>
                <button 
                    class="btn btn--filter" 
                    :class="{ active: filter === 'monthly' }" 
                    @click="filter = 'monthly'; applyFilter();">
                    {{ labelsState['monthly'] || 'Monthly' }}
                </button>
                <button 
                    class="btn btn--filter" 
                    :class="{ active: filter === 'annual' }" 
                    @click="filter = 'annual'; applyFilter();">
                    {{ labelsState['annual'] || 'Annual' }}
                </button>
                <button
                    class="btn btn--filter"
                    :class="{ active: filter === 'infrastructure' }"
                    @click="filter = 'infrastructure'; applyFilter();">
                    {{ labelsState['infrastructure'] || 'Infrastructure' }}
                </button>
            </div>
        </div>
        <div v-if="loading" class="row justify-content-center">
            <div class="col-auto pricing-status">
                <i class="fa-solid fa-spinner fa-spin"></i>
                {{ labelsState['loading'] || 'Loading plans...' }}
            </div>
        </div>
        <div v-else-if="!filteredPlans.length" class="row justify-content-center">
            <div class="col-auto pricing-status">
                {{ labelsState['no_plans_available'] || 'No plans available in this category.' }}
            </div>
        </div>
        <div v-else class="row items-gap justify-content-center">
            <div
                class="col-12 col-md-6 col-lg-6 col-xxl-4"
                v-for="plan in filteredPlans"
                :key="plan.id">
                <div class="pricing-two__single wow fadeInUp" 
     :class="{ 'highlighted-plan': plan.is_highlighted }" 
     data-wow-duration="600ms" 
     data-wow-delay="900ms">
                    <div class="title-header">
                        <h5 class="h5 yel plan-title">{{ plan.subscription_plan }}</h5>
                        <span class="frequency-badge">{{ plan.frequency }} {{ labelsState['month_s'] || 'Month(s)' }}</span>
                        <div class="plan-tags" v-if="plan.trial_period_days || plan.free_months">
                            <span class="plan-tag trial-tag" v-if="plan.trial_period_days">
                                ✨ {{ plan.trial_period_days }} {{ labelsState['days_trial'] || 'Days Trial' }}
                            </span>
                            <span class="plan-tag free-months-tag" v-if="plan.free_months">
                                🎁 {{ plan.free_months }} {{ labelsState['free_months'] || 'Free Month(s)' }}
                            </span>
                        </div>
                    </div>
                    <div class="meta">
                        <div class="content">
                            <!-- USD Price -->
                            <div class="price-row">
                                <div class="price-container">
                                    <h2 class="price discount-price" v-if="plan.discount_amount_usd">
                                        ${{ formatNumber(plan.discount_amount_usd) }}
                                    </h2>
                                    <h2 class="price" :class="{ 'discounted': plan.discount_amount_usd }">
                                        {{ plan.amount ? '$' + formatNumber(plan.amount) : 'Free' }} 
                                        <sup class="cop-super">USD</sup>
                                    </h2>
                                </div>
                                <span class="tax-inline" v-if="plan.amount">+ {{ labelsState['tax'] || 'taxes' }}</span>
                            </div>
                            
                            <!-- COP Price -->
                            <div class="price-row">
                                <div class="price-container">
                                    <h2 class="price discount-price" v-if="plan.discount_amount_cop">
                                        ${{ formatNumber(plan.discount_amount_cop) }}
                                    </h2>
                                    <h2 class="price" :class="{ 'discounted': plan.discount_amount_cop }">
                                        {{ plan.amount_cop ? '$' + formatNumber(plan.amount_cop) : 'Free' }} 
                                        <sup class="cop-super">COP</sup>
                                    </h2>
                                </div>
                                <span class="tax-inline" v-if="plan.amount_cop">+ {{ labelsState['tax'] || 'taxes' }}</span>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Setup inicial (one-time fee) -->
                    <div class="setup-fee-box" v-if="plan.setup_fee || plan.setup_fee_cop">
                        <i class="fa-solid fa-gear"></i>
                        <span class="setup-fee-label">{{ labelsState['setup_fee'] || 'Setup Fee' }}:</span>
                        <span class="setup-fee-value" v-if="plan.setup_fee">${{ formatNumber(plan.setup_fee) }} USD</span>
                        <span v-if="plan.setup_fee && plan.setup_fee_cop"> / </span>
                        <span class="setup-fee-value" v-if="plan.setup_fee_cop">${{ formatNumber(plan.setup_fee_cop) }} COP</span>
                        <span class="setup-fee-note">({{ labelsState['one_time_payment'] || 'one-time payment' }})</span>
                    </div>

                    <hr v-if="plan.contacts > 0 || plan.credits > 0">
                    <!-- Contact-based plan -->
                    <div class="contacts-highlight" v-if="plan.contacts > 0">
                        <i class="fa-solid fa-address-book"></i>
                        <span class="credits-number">{{ formatNumber(plan.contacts) }}</span>
                        <span class="credits-label">{{ labelsState['contacts'] || 'Contacts' }}</span>
                    </div>
                    <!-- Credit-based plan -->
                    <div class="credits-highlight" v-else-if="plan.credits > 0">
                        <i class="fa-solid fa-coins"></i>
                        <span class="credits-number">{{ formatNumber(plan.credits) }}</span>
                        <span class="credits-label">{{ labelsState['credits'] || 'credits' }}</span>
                    </div>
                    <hr v-if="plan.contacts > 0 || plan.credits > 0">
                    <p class="text-white-dark mt-2" v-html="plan.description"></p>
                    <hr>
                    <a 
                        :href="subsUrl"
                        target="_blank" 
                        class="btn btn--septenary">
                        {{ plan.is_free ? (labelsState['free_demo'] || 'Free Demo') : (labelsState['suscribe_now'] || 'Subscribe Now') }}
                    </a>
                </div>
            </div>
        </div>
    </div>
</section>
</template>

<script>
import { useLabelsStore } from '@/stores/labels';
import { useLanguageStore } from '@/stores/language';
import { computed, onMounted, ref } from 'vue';
import axios from 'axios';

export default {
    name: "Pricing",
    setup() {
        const subsUrl = `${process.env.VUE_APP_DASHBOARD_URL}/subscriptions`;
        const labelsStore = useLabelsStore();
        const languageStore = useLanguageStore();
        const plans = ref([]);
        const filteredPlans = ref([]);
        const filter = ref("monthly");
        const loading = ref(true);

        const fetchPlans = async () => {
            try {
                const response = await axios.get(`${process.env.VUE_APP_HOST_BILLING}/plans_open`);
                plans.value = response.data.plans.sort((a, b) => {
                    if (a.is_free && !b.is_free) return -1;
                    if (!a.is_free && b.is_free) return 1;

                    if (a.is_enterprise && !b.is_enterprise) return 1;
                    if (!a.is_enterprise && b.is_enterprise) return -1;

                    if (a.is_infrastructure && !b.is_infrastructure) return 1;
                    if (!a.is_infrastructure && b.is_infrastructure) return -1;

                    if (a.frequency !== b.frequency) {return a.frequency - b.frequency;}
                    if (a.amount !== b.amount) {return (a.amount || 0) - (b.amount || 0);}
                    return 0;
                });
                applyFilter();
    
            } catch (error) {
                console.error("Error fetching plans:", error);
            } finally {
                loading.value = false;
            }
        };

        const applyFilter = () => {
            let filtered = [];
            if (filter.value === "all") {
                filtered = plans.value;
            } else if (filter.value === "free") {
                filtered = plans.value.filter(plan => plan.is_free);
            } else if (filter.value === "monthly") {
                filtered = plans.value.filter(
                    plan => plan.frequency === 1 && !plan.is_free && !plan.is_enterprise && !plan.is_infrastructure
                );
            } else if (filter.value === "annual") {
                filtered = plans.value.filter(
                    plan => plan.frequency === 12 && !plan.is_free && !plan.is_enterprise && !plan.is_infrastructure
                );
            } else if (filter.value === "enterprise") {
                filtered = plans.value.filter(plan => plan.is_enterprise);
            } else if (filter.value === "infrastructure") {
                filtered = plans.value.filter(plan => plan.is_infrastructure);
            }
            
            filteredPlans.value = filtered.sort((a, b) => {
                const aHasCredits = a.credits && a.credits > 0;
                const bHasCredits = b.credits && b.credits > 0;
                if (aHasCredits && !bHasCredits) return -1;
                if (!aHasCredits && bHasCredits) return 1;
                return 0;
            });
        };

        onMounted(() => {
            fetchPlans();
        });

        const labelsState = computed(() => labelsStore.labels);
        const languageCodes = computed(() => languageStore.codes);
        
        function formatNumber(value) {
            if (value === null || value === undefined) return '0';
            return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        }

        return {
            labelsState,
            languageCodes,
            plans,
            filteredPlans,
            loading,
            subsUrl,
            filter,
            applyFilter,
            formatNumber,
        };
    },
};
</script>

<style>

.price-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
}

.discount-price {
    text-decoration: line-through;
    color: #9ca3af !important;
    font-size: 1.2rem !important;
    opacity: 0.7;
}

.price.discounted {
    color: #00CC99 !important;
    font-weight: 900 !important;
}

.pricing-two__single.highlighted-plan {
    border: 3px solid #00CC99;
    box-shadow: 0 0 30px rgba(0, 204, 153, 0.4);
    transform: scale(1.02);
    position: relative;
}

.pricing-two__single.highlighted-plan::before {
    content: "⭐ POPULAR";
    position: absolute;
    top: -15px;
    right: 20px;
    background: linear-gradient(90deg, #00CC99 0%, #003366 100%);
    color: white;
    padding: 5px 15px;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: bold;
    letter-spacing: 1px;
}

.pricing-two__single.highlighted-plan:hover {
    box-shadow: 0 0 40px rgba(0, 204, 153, 0.6);
}

.pricing-two {
    background-color: #111827;
}

.text-center {
    text-align: center;
}

.title-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    margin-bottom: 0rem;
    padding: 0.5rem 0;
}

.plan-title {
    font-size: 1.8rem;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 1px;
}

.frequency-badge {
    font-size: 0.7rem;
    color: #9ca3af;
    background: rgba(156, 163, 175, 0.1);
    padding: 0.2rem 0.2rem;
    border-radius: 4px;
}

.price-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
}

.price {
    font-size: 1.5rem;
    margin: 0;
    font-weight: bold;
    color: white;
}

.tax-inline {
    font-size: 0.65rem;
    color: white;
    opacity: 0.6;
}

.meta {
    flex-direction: column;
}

.cop-super {
    font-size: 0.5em;
    vertical-align: super;
    color: white;
}

.meta .content {
    flex-direction: column;
}

.credits-highlight {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    background: linear-gradient(135deg, rgba(0, 204, 153, 0.1) 0%, rgba(0, 51, 102, 0.1) 100%);
    border-radius: 10px;
   
}

.credits-highlight i {
    font-size: 2rem;
    color: #00CC99;
    margin-bottom: 0.3rem;
}

.contacts-highlight {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    background: linear-gradient(135deg, rgba(0, 204, 153, 0.1) 0%, rgba(0, 51, 102, 0.1) 100%);
    border-radius: 10px;
}

.contacts-highlight i {
    font-size: 2rem;
    color: #00CC99;
    margin-bottom: 0.3rem;
}

.pricing-two__single:hover .contacts-highlight .credits-number {
    color: white;
}

.setup-fee-box {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 0.3rem;
    margin: 0.5rem 0;
    padding: 0.5rem 0.75rem;
    background: rgba(245, 158, 11, 0.18);
    border: 1px solid rgba(245, 158, 11, 0.55);
    border-radius: 8px;
    font-size: 0.8rem;
    font-weight: 700;
}

.setup-fee-box,
.setup-fee-box span,
.setup-fee-box i {
    color: #fde68a;
}

.setup-fee-label {
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.setup-fee-value {
    color: #fff;
}

.setup-fee-note {
    color: #fde68a;
    font-weight: 500;
    opacity: 0.85;
    font-size: 0.72rem;
}

.plan-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    justify-content: center;
    margin-top: 0.4rem;
}

.plan-tag {
    font-size: 0.7rem;
    font-weight: 600;
    padding: 0.2rem 0.55rem;
    border-radius: 999px;
    line-height: 1.2;
}

.trial-tag {
    background: rgba(59, 130, 246, 0.15);
    color: #93c5fd;
    border: 1px solid rgba(59, 130, 246, 0.35);
}

.free-months-tag {
    background: rgba(16, 185, 129, 0.15);
    color: #6ee7b7;
    border: 1px solid rgba(16, 185, 129, 0.35);
}

.pricing-status {
    text-align: center;
    color: #d1d5db;
    font-size: 1rem;
    padding: 2.5rem 1rem;
}

.pricing-status i {
    color: #00CC99;
    margin-right: 0.5rem;
}

.credits-number {
    font-size: 2.5rem;
    font-weight: bold;
    color: #00CC99;
    line-height: 1;
    margin: 0.2rem 0;
}

.credits-label {
    font-size: 0.85rem;
    color: #d1d5db;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-top: 0.2rem;
    transition: color 0.3s ease;
}

.pricing-two__single:hover .credits-number {
    color: white;
}

ul {
    list-style: none;
    padding: 0;
    color: white;
}

ul li {
    margin: 0.5rem 0;
}

h2.price {
    display: block;
    text-align: center;
    margin: 0 auto;
}

.pricing-two__single {
    padding: 15px;
    background-color: #1f2937;
    border-radius: 10px;
    height: 100%;
}

.pricing-two__single hr {
    margin: 0.5rem 0;
}

.pricing-two__single .mt-2 {
    margin-top: 0.5rem;
}

.btn--filter {
    padding: 10px 20px;
    margin: 5px;
    border: 1px solid #00CC99;
    color: #00CC99;
    background: transparent;
    cursor: pointer;
    transition: background-color 0.3s ease, color 0.3s ease;
}

.btn--filter:hover {
    background-image: linear-gradient(90deg, #00CC99 0%, #003366 95.62%);
    color: white;
}

.btn--filter.active {
    background-color: #00CC99;
    color: #fff;
    font-weight: bold; 
    border: 1px solid #00CC99;
}

.text-white-dark strong {
    transition: color 0.3s ease;
}

.pricing-two__single:hover .text-white-dark strong {
    color: white !important;
}
</style>